// Cache simple en memoria para alturas de posts y de medias por clave
// Claves sugeridas:
// - Post: `${postId}`
// - Media: `${postId}:${mediaIndex}`
// Añadimos persistencia con AsyncStorage (hidratar temprano en App y guardado diferido)

import AsyncStorage from '@react-native-async-storage/async-storage'

const postHeights = new Map()
const mediaHeights = new Map()
// Altura máxima observada de la(s) media(s) principal(es) de un post (sin header/interacciones/texto)
const mediaMaxHeights = new Map()
const mediaRatiosByUrl = new Map()

const STORAGE_KEY = 'SizeCache:v1'
let hydrated = false
let saveTimer = null
const SAVE_DEBOUNCE_MS = 800

const serialize = () => {
  try {
    return JSON.stringify({
      post: Array.from(postHeights.entries()),
      media: Array.from(mediaHeights.entries()),
      mediaMax: Array.from(mediaMaxHeights.entries()),
      ratios: Array.from(mediaRatiosByUrl.entries())
    })
  } catch (e) {
    // Si falla la serialización, retornamos un objeto vacío para no romper
    return JSON.stringify({ post: [], media: [], ratios: [] })
  }
}

const scheduleSave = () => {
  try {
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(async () => {
      saveTimer = null
      try {
        await AsyncStorage.setItem(STORAGE_KEY, serialize())
      } catch (_) {
        // noop: no queremos romper el hilo de UI si falla el guardado
      }
    }, SAVE_DEBOUNCE_MS)
  } catch (_) {
    // noop
  }
}

const hydrateInternal = async () => {
  if (hydrated) return
  hydrated = true
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed?.post)) {
      for (const [k, v] of parsed.post) {
        if (typeof v === 'number' && v > 0) postHeights.set(String(k), v)
      }
    }
    if (Array.isArray(parsed?.media)) {
      for (const [k, v] of parsed.media) {
        if (typeof v === 'number' && v > 0) mediaHeights.set(String(k), v)
      }
    }
    if (Array.isArray(parsed?.mediaMax)) {
      for (const [k, v] of parsed.mediaMax) {
        if (typeof v === 'number' && v > 0) mediaMaxHeights.set(String(k), v)
      }
    }
    if (Array.isArray(parsed?.ratios)) {
      for (const [k, v] of parsed.ratios) {
        const r = Number(v)
        if (isFinite(r) && r > 0) mediaRatiosByUrl.set(String(k), r)
      }
    }
  } catch (_) {
    // noop: si falla la hidratación, seguimos con mapas vacíos
  }
}

export const SizeCache = {
  // Hidratar desde AsyncStorage (idempotente)
  hydrate: async () => {
    await hydrateInternal()
  },
  // Media principal (altura máxima sin extras UI)
  getPostMediaMaxHeight: (postId) => {
    if (postId == null) return null
    return mediaMaxHeights.get(String(postId)) || null
  },
  setPostMediaMaxHeight: (postId, h) => {
    if (postId == null) return
    if (typeof h !== 'number' || !(h > 0)) return
    const key = String(postId)
    const prev = mediaMaxHeights.get(key)
    if (prev === h) return
    // Si la altura previa es mayor y la nueva es menor, conservamos la mayor para evitar jumps hacia arriba
    if (prev && prev > h) return
    mediaMaxHeights.set(key, h)
    scheduleSave()
  },
  // Forzar guardado inmediato (útil en casos puntuales)
  flush: async () => {
    if (saveTimer) {
      clearTimeout(saveTimer)
      saveTimer = null
    }
    try {
      await AsyncStorage.setItem(STORAGE_KEY, serialize())
    } catch (_) {
      // noop
    }
  },
  // Post
  getPostHeight: (postId) => {
    if (postId == null) return null
    return postHeights.get(String(postId)) || null
  },
  setPostHeight: (postId, h) => {
    if (postId == null) return
    if (typeof h !== 'number' || !(h > 0)) return
    const key = String(postId)
    const prev = postHeights.get(key)
    if (prev === h) return
    postHeights.set(key, h)
    scheduleSave()
  },
  // Media
  getMediaHeight: (key) => {
    if (!key) return null
    return mediaHeights.get(String(key)) || null
  },
  setMediaHeight: (key, h) => {
    if (!key) return
    if (typeof h !== 'number' || !(h > 0)) return
    const k = String(key)
    const prev = mediaHeights.get(k)
    if (prev === h) return
    mediaHeights.set(k, h)
    scheduleSave()
  },
  // Ratios por URL
  getRatioForUrl: (url) => {
    if (!url) return null
    return mediaRatiosByUrl.get(String(url)) || null
  },
  setRatioForUrl: (url, ratio) => {
    if (!url) return
    const r = Number(ratio)
    if (!isFinite(r) || !(r > 0)) return
    const k = String(url)
    const prev = mediaRatiosByUrl.get(k)
    if (prev === r) return
    mediaRatiosByUrl.set(k, r)
    scheduleSave()
  },
  // Limpieza opcional
  clear: () => {
    postHeights.clear()
    mediaHeights.clear()
    mediaRatiosByUrl.clear()
    try {
      AsyncStorage.removeItem(STORAGE_KEY)
    } catch (_) {
      // noop
    }
  }
}

export default SizeCache
